import type { CreateLabelInput, LabelDto, UpdateLabelInput } from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export const labelsQueryOptions = (projectCode: string) =>
  queryOptions({
    queryKey: queryKeys.labels.list(projectCode),
    queryFn: ({ signal }) =>
      api.get<LabelDto[]>(`/projects/${projectCode}/labels`, undefined, signal),
  });

export const useLabels = (projectCode: string) => useQuery(labelsQueryOptions(projectCode));

/** A label change can appear on any case, so the case lists go stale with it. */
const useLabelInvalidation = (projectCode: string) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.labels.list(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.testCases.all(projectCode) });
  };
};

export const useCreateLabel = (projectCode: string) => {
  const invalidate = useLabelInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: CreateLabelInput) =>
      api.post<LabelDto>(`/projects/${projectCode}/labels`, input),
    onSuccess: invalidate,
  });
};

export const useUpdateLabel = (projectCode: string) => {
  const invalidate = useLabelInvalidation(projectCode);
  return useMutation({
    mutationFn: ({ labelId, input }: { labelId: string; input: UpdateLabelInput }) =>
      api.patch<LabelDto>(`/projects/${projectCode}/labels/${labelId}`, input),
    onSuccess: invalidate,
  });
};

export const useDeleteLabel = (projectCode: string) => {
  const invalidate = useLabelInvalidation(projectCode);
  return useMutation({
    mutationFn: (labelId: string) => api.delete(`/projects/${projectCode}/labels/${labelId}`),
    onSuccess: invalidate,
  });
};
